import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Tooltip,
    InputAdornment,
    Menu,
    ListItemIcon,
    ListItemText,
    Snackbar,
    CircularProgress
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    MoreVert as MoreVertIcon,
    VpnKey as KeyIcon,
    Computer as ComputerIcon,
    Person as PersonIcon,
    AdminPanelSettings as AdminIcon,
    SupervisedUserCircle as ManagerIcon,
    AccountCircle as UserIcon,
    Visibility as ViewerIcon,
    Check as CheckIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import authService from '../services/auth.service';

interface User {
    _id: string;
    email: string;
    fullName: string;
    role: 'admin' | 'manager' | 'user' | 'viewer';
    department?: string;
    status: 'active' | 'inactive';
    createdAt: string;
    lastActive?: string;
    machineCount?: number;
}

interface UserFormData {
    email: string;
    password: string;
    fullName: string;
    role: 'admin' | 'manager' | 'user' | 'viewer';
    department: string;
    generateDesktopCredentials: boolean;
}

const Users: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalUsers, setTotalUsers] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [roleFilter, setRoleFilter] = useState<string>('all');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [formData, setFormData] = useState<UserFormData>({
        email: '',
        password: '',
        fullName: '',
        role: 'user',
        department: '',
        generateDesktopCredentials: false
    });
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success'
    });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);
    const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [credentialsDialog, setCredentialsDialog] = useState<{ open: boolean; credentials?: any }>({
        open: false
    });

    useEffect(() => {
        fetchUsers();
    }, [page, rowsPerPage, searchQuery, roleFilter, statusFilter]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const api = authService.getApiInstance();
            const params = new URLSearchParams({
                page: (page + 1).toString(),
                limit: rowsPerPage.toString(),
                ...(searchQuery && { search: searchQuery }),
                ...(roleFilter !== 'all' && { role: roleFilter }),
                ...(statusFilter !== 'all' && { status: statusFilter })
            });

            const response = await api.get(`/users?${params}`);
            setUsers(response.data.users);
            setTotalUsers(response.data.pagination.total);
        } catch (error) {
            console.error('Failed to fetch users:', error);
            showSnackbar('Failed to fetch users', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUser = async () => {
        try {
            const api = authService.getApiInstance();
            const response = await api.post('/users', formData);
            
            if (response.data.credentials) {
                setCredentialsDialog({
                    open: true,
                    credentials: response.data.credentials
                });
            }
            
            showSnackbar('User created successfully', 'success');
            setDialogOpen(false);
            resetForm();
            fetchUsers();
        } catch (error: any) {
            showSnackbar(error.response?.data?.error || 'Failed to create user', 'error');
        }
    };

    const handleUpdateUser = async () => {
        if (!editingUser) return;

        try {
            const api = authService.getApiInstance();
            const updateData = {
                fullName: formData.fullName,
                role: formData.role,
                department: formData.department
            };

            await api.put(`/users/${editingUser._id}`, updateData);
            showSnackbar('User updated successfully', 'success');
            setDialogOpen(false);
            resetForm();
            fetchUsers();
        } catch (error: any) {
            showSnackbar(error.response?.data?.error || 'Failed to update user', 'error');
        }
    };

    const handleDeleteUser = async () => {
        if (!userToDelete) return;

        try {
            const api = authService.getApiInstance();
            await api.delete(`/users/${userToDelete._id}`);
            showSnackbar('User deleted successfully', 'success');
            setDeleteDialogOpen(false);
            setUserToDelete(null);
            fetchUsers();
        } catch (error: any) {
            showSnackbar(error.response?.data?.error || 'Failed to delete user', 'error');
        }
    };

    const handleResetPassword = async () => {
        if (!selectedUser || !newPassword) return;

        try {
            const api = authService.getApiInstance();
            await api.post(`/users/${selectedUser._id}/reset-password`, { newPassword });
            showSnackbar('Password reset successfully', 'success');
            setResetPasswordDialogOpen(false);
            setNewPassword('');
            setSelectedUser(null);
        } catch (error: any) {
            showSnackbar(error.response?.data?.error || 'Failed to reset password', 'error');
        }
    };

    const handleGenerateCredentials = async (user: User) => {
        try {
            const api = authService.getApiInstance();
            const response = await api.post(`/users/${user._id}/generate-desktop-credentials`);
            setCredentialsDialog({
                open: true,
                credentials: response.data.credentials
            });
            showSnackbar('Desktop credentials generated successfully', 'success');
        } catch (error: any) {
            showSnackbar(error.response?.data?.error || 'Failed to generate credentials', 'error');
        }
    };

    const handleToggleStatus = async (user: User) => {
        try {
            const api = authService.getApiInstance();
            await api.put(`/users/${user._id}`, {
                status: user.status === 'active' ? 'inactive' : 'active'
            });
            showSnackbar(`User ${user.status === 'active' ? 'deactivated' : 'activated'} successfully`, 'success');
            fetchUsers();
        } catch (error: any) {
            showSnackbar(error.response?.data?.error || 'Failed to update user status', 'error');
        }
    };

    const resetForm = () => {
        setFormData({
            email: '',
            password: '',
            fullName: '',
            role: 'user',
            department: '',
            generateDesktopCredentials: false
        });
        setEditingUser(null);
    };

    const showSnackbar = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    const getRoleIcon = (role: string) => {
        switch (role) {
            case 'admin':
                return <AdminIcon />;
            case 'manager':
                return <ManagerIcon />;
            case 'user':
                return <UserIcon />;
            case 'viewer':
                return <ViewerIcon />;
            default:
                return <PersonIcon />;
        }
    };

    const getRoleColor = (role: string): 'error' | 'warning' | 'info' | 'default' => {
        switch (role) {
            case 'admin':
                return 'error';
            case 'manager':
                return 'warning';
            case 'user':
                return 'info';
            case 'viewer':
                return 'default';
            default:
                return 'default';
        }
    };

    const formatDate = (date: string) => {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                User Management
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Manage system users and their permissions
            </Typography>

            <Paper sx={{ mb: 3, p: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        placeholder="Search users..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ flex: 1, minWidth: 300 }}
                    />
                    <FormControl sx={{ minWidth: 120 }}>
                        <InputLabel>Role</InputLabel>
                        <Select
                            value={roleFilter}
                            onChange={(e) => setRoleFilter(e.target.value)}
                            label="Role"
                        >
                            <MenuItem value="all">All Roles</MenuItem>
                            <MenuItem value="admin">Admin</MenuItem>
                            <MenuItem value="manager">Manager</MenuItem>
                            <MenuItem value="user">User</MenuItem>
                            <MenuItem value="viewer">Viewer</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 120 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            label="Status"
                        >
                            <MenuItem value="all">All Status</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="inactive">Inactive</MenuItem>
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            resetForm();
                            setDialogOpen(true);
                        }}
                    >
                        Add User
                    </Button>
                </Box>
            </Paper>

            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>User</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>Department</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Machines</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : users.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        No users found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                users.map((user) => (
                                    <TableRow key={user._id}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {getRoleIcon(user.role)}
                                                <Typography variant="body2">{user.fullName}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={user.role}
                                                size="small"
                                                color={getRoleColor(user.role)}
                                            />
                                        </TableCell>
                                        <TableCell>{user.department || '-'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={user.status === 'active' ? <CheckIcon /> : <CloseIcon />}
                                                label={user.status}
                                                size="small"
                                                color={user.status === 'active' ? 'success' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <ComputerIcon fontSize="small" />
                                                {user.machineCount || 0}
                                            </Box>
                                        </TableCell>
                                        <TableCell>{formatDate(user.createdAt)}</TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                onClick={(e) => {
                                                    setSelectedUser(user);
                                                    setAnchorEl(e.currentTarget);
                                                }}
                                            >
                                                <MoreVertIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={totalUsers}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>

            {/* User Actions Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => {
                    setAnchorEl(null);
                    setSelectedUser(null);
                }}
            >
                <MenuItem
                    onClick={() => {
                        if (selectedUser) {
                            setEditingUser(selectedUser);
                            setFormData({
                                ...formData,
                                fullName: selectedUser.fullName,
                                role: selectedUser.role,
                                department: selectedUser.department || ''
                            });
                            setDialogOpen(true);
                            setAnchorEl(null);
                        }
                    }}
                >
                    <ListItemIcon>
                        <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Edit User</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        if (selectedUser) {
                            handleToggleStatus(selectedUser);
                            setAnchorEl(null);
                        }
                    }}
                >
                    <ListItemIcon>
                        {selectedUser?.status === 'active' ? <CloseIcon fontSize="small" /> : <CheckIcon fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText>
                        {selectedUser?.status === 'active' ? 'Deactivate' : 'Activate'}
                    </ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setResetPasswordDialogOpen(true);
                        setAnchorEl(null);
                    }}
                >
                    <ListItemIcon>
                        <KeyIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Reset Password</ListItemText>
                </MenuItem>
                {/* <MenuItem
                    onClick={() => {
                        if (selectedUser) {
                            handleGenerateCredentials(selectedUser);
                            setAnchorEl(null);
                        }
                    }}
                >
                    <ListItemIcon>
                        <ComputerIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Generate Desktop Credentials</ListItemText>
                </MenuItem> */}
                <MenuItem
                    onClick={() => {
                        if (selectedUser) {
                            setUserToDelete(selectedUser);
                            setDeleteDialogOpen(true);
                            setAnchorEl(null);
                        }
                    }}
                    sx={{ color: 'error.main' }}
                >
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText>Delete User</ListItemText>
                </MenuItem>
            </Menu>

            {/* Create/Edit User Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingUser ? 'Edit User' : 'Create New User'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        {!editingUser && (
                            <>
                                <TextField
                                    label="Email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    fullWidth
                                    required
                                />
                                <TextField
                                    label="Password"
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    fullWidth
                                    required
                                    helperText="Minimum 6 characters"
                                />
                            </>
                        )}
                        <TextField
                            label="Full Name"
                            value={formData.fullName}
                            onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                            fullWidth
                            required
                        />
                        <FormControl fullWidth required>
                            <InputLabel>Role</InputLabel>
                            <Select
                                value={formData.role}
                                onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                                label="Role"
                            >
                                <MenuItem value="admin">Admin</MenuItem>
                                <MenuItem value="manager">Manager</MenuItem>
                                <MenuItem value="user">User</MenuItem>
                                <MenuItem value="viewer">Viewer</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Department"
                            value={formData.department}
                            onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                            fullWidth
                        />
                        {/* {!editingUser && (
                            <FormControl>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={formData.generateDesktopCredentials}
                                        onChange={(e) => setFormData({ ...formData, generateDesktopCredentials: e.target.checked })}
                                    />
                                    {' '}Generate desktop application credentials
                                </label>
                            </FormControl>
                        )} */}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={editingUser ? handleUpdateUser : handleCreateUser}
                    >
                        {editingUser ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete User</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This action cannot be undone.
                    </Alert>
                    <Typography>
                        Are you sure you want to delete user "{userToDelete?.fullName}"?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteUser}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reset Password Dialog */}
            <Dialog open={resetPasswordDialogOpen} onClose={() => setResetPasswordDialogOpen(false)}>
                <DialogTitle>Reset Password</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Reset password for {selectedUser?.fullName}
                    </Typography>
                    <TextField
                        label="New Password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        fullWidth
                        required
                        helperText="Minimum 6 characters"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResetPasswordDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleResetPassword}>
                        Reset Password
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Credentials Display Dialog */}
            <Dialog open={credentialsDialog.open} onClose={() => setCredentialsDialog({ open: false })}>
                <DialogTitle>Desktop Application Credentials</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Save these credentials securely. They will not be shown again.
                    </Alert>
                    {credentialsDialog.credentials && (
                        <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace' }}>
                            <Typography variant="body2">
                                API Key: {credentialsDialog.credentials.apiKey}
                            </Typography>
                            <Typography variant="body2">
                                API Secret: {credentialsDialog.credentials.apiSecret}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={() => setCredentialsDialog({ open: false })}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Users;